import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { OperationWaterfall } from "./operation-waterfall";
import { operationTitle } from "./printer-labels";
import type { PrinterOperation } from "./printers-api";
import { printerErrorMessage, usePrinterOperation } from "./use-printers-query";

/** Odczyt operacji po `operationId` (odpytuje, gdy trwa) albo z gotowego wiersza historii. */
export function OperationStatusView({
	operationId,
	initial,
	poll = true,
}: {
	operationId: number;
	initial?: PrinterOperation | null;
	/** `false` — operację odpytuje już wykonawca planu, widok czyta tylko pamięć podręczną. */
	poll?: boolean;
}) {
	const { operation, isError, error } = usePrinterOperation(operationId, initial, poll);
	if (operation) return <OperationWaterfall operation={operation} />;
	if (isError) {
		return (
			<p role="alert" className="text-sm text-status-error-text">
				Nie udało się pobrać stanu operacji: {printerErrorMessage(error)}
			</p>
		);
	}
	return (
		<p className="flex items-center gap-2 text-sm text-muted-foreground">
			<Loader2 className="size-4 animate-spin" aria-hidden />
			Pobieranie stanu operacji…
		</p>
	);
}

/**
 * Okno wodospadu operacji: trwająca operacja (np. rozpoczęta w innej karcie)
 * i podgląd operacji z historii. Zamknięcie nie przerywa operacji — robi ją
 * backend i agent, panel tylko obserwuje.
 */
export function OperationDialog({
	operationId,
	initial,
	onClose,
}: {
	operationId: number | null;
	initial?: PrinterOperation | null;
	onClose: () => void;
}) {
	const open = operationId != null;
	return (
		<Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
			<DialogContent className="max-w-xl">
				<DialogHeader>
					<DialogTitle>{initial ? operationTitle(initial.kind) : "Postęp operacji"}</DialogTitle>
					<DialogDescription>
						{/* Bez „apteki”: okno pokazuje też operacje grupy urządzeń bez apteki. */}
						{initial
							? "Kroki wykonane na komputerach."
							: "Kroki wykonywane na komputerach — odświeżane co 2 sekundy. Zamknięcie okna nie przerywa operacji."}
					</DialogDescription>
				</DialogHeader>
				<DialogBody>
					{operationId != null ? (
						<OperationStatusView operationId={operationId} initial={initial} />
					) : null}
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" size="sm" onClick={onClose}>
						Zamknij
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
